import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSystemAlerts, getAlertsSummary, resolveAlert, bulkResolveAlerts, deleteAlert } from '../../services/adminApi';
import { notify, confirmAsync } from '../../utils/notify';
import './SystemAlerts.css';

const SEVERITY_CONFIG = {
  critical: { icon: '🔴', label: 'Critical', className: 'severity-critical' },
  error:    { icon: '🟠', label: 'Error',    className: 'severity-error' },
  warning:  { icon: '🟡', label: 'Warning',  className: 'severity-warning' },
};

const CATEGORY_CONFIG = {
  whatsapp:   { icon: '💬', label: 'WhatsApp' },
  webhook:    { icon: '🔗', label: 'Webhook' },
  database:   { icon: '🗄️', label: 'Database' },
  llm:        { icon: '🤖', label: 'LLM / AI' },
  social_api: { icon: '📱', label: 'Social API' },
  escalation: { icon: '🚨', label: 'Escalation' },
  system:     { icon: '⚙️', label: 'System' },
};

const AUTO_REFRESH_INTERVAL_MS = 30000;

function SystemAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [showResolveModal, setShowResolveModal] = useState(null);
  const pageSize = 30;
  const refreshTimer = useRef(null);

  const [filters, setFilters] = useState({
    category: '',
    severity: '',
    is_resolved: false,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (filters.category) params.category = filters.category;
      if (filters.severity) params.severity = filters.severity;
      if (filters.is_resolved !== null && filters.is_resolved !== '') {
        params.is_resolved = filters.is_resolved;
      }

      const [alertsRes, summaryRes] = await Promise.all([
        getSystemAlerts(params),
        getAlertsSummary(),
      ]);

      setAlerts(alertsRes.data.alerts || []);
      setTotal(alertsRes.data.total || 0);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh
  useEffect(() => {
    refreshTimer.current = setInterval(loadData, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshTimer.current);
  }, [loadData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
    setSelectedAlerts(new Set());
  };

  const toggleSelectAlert = (id) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAlerts.size === alerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(alerts.filter(a => !a.is_resolved).map(a => a.id)));
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await resolveAlert(alertId, resolveNote || null);
      setShowResolveModal(null);
      setResolveNote('');
      await loadData();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
      notify.error('Failed to resolve alert');
    }
  };

  const handleBulkResolve = async () => {
    if (selectedAlerts.size === 0) return;
    try {
      await bulkResolveAlerts(Array.from(selectedAlerts), resolveNote || null);
      setSelectedAlerts(new Set());
      setResolveNote('');
      await loadData();
    } catch (err) {
      console.error('Failed to bulk resolve:', err);
      notify.error('Failed to bulk resolve alerts');
    }
  };

  const handleDelete = async (alertId) => {
    if (!(await confirmAsync({ message: 'Delete this alert permanently?', danger: true }))) return;
    try {
      await deleteAlert(alertId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete alert:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="system-alerts">
      <div className="alerts-header">
        <h2>System Alerts</h2>
        <div className="alerts-meta">
          <span className="auto-refresh-badge">Auto-refresh: 30s</span>
          <button className="btn-refresh" onClick={loadData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="alerts-summary">
          <div className="summary-card summary-total">
            <span className="summary-number">{summary.total_unresolved}</span>
            <span className="summary-label">Unresolved</span>
          </div>
          <div className="summary-card summary-critical">
            <span className="summary-number">{summary.critical_count}</span>
            <span className="summary-label">Critical</span>
          </div>
          {summary.by_category?.map(cat => (
            <div className="summary-card summary-category" key={cat.category}>
              <span className="summary-icon">{CATEGORY_CONFIG[cat.category]?.icon || '❓'}</span>
              <span className="summary-number">{cat.count}</span>
              <span className="summary-label">{CATEGORY_CONFIG[cat.category]?.label || cat.category}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="alerts-filters">
        <select
          value={filters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className="filter-select"
        >
          <option value="">All categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
          ))}
        </select>

        <select
          value={filters.severity}
          onChange={(e) => handleFilterChange('severity', e.target.value)}
          className="filter-select"
        >
          <option value="">All severities</option>
          {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
          ))}
        </select>

        <select
          value={filters.is_resolved === null ? '' : String(filters.is_resolved)}
          onChange={(e) => {
            const val = e.target.value;
            handleFilterChange('is_resolved', val === '' ? null : val === 'true');
          }}
          className="filter-select"
        >
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
          <option value="">All</option>
        </select>

        {selectedAlerts.size > 0 && (
          <button className="btn-bulk-resolve" onClick={handleBulkResolve}>
            Resolve {selectedAlerts.size} selected
          </button>
        )}
      </div>

      {/* Alerts table */}
      {loading ? (
        <div className="alerts-loading">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="alerts-empty">No alerts found</div>
      ) : (
        <>
          <table className="alerts-table">
            <thead>
              <tr>
                <th className="col-select">
                  <input
                    type="checkbox"
                    checked={selectedAlerts.size === alerts.filter(a => !a.is_resolved).length && alerts.filter(a => !a.is_resolved).length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="col-severity">Severity</th>
                <th className="col-category">Category</th>
                <th className="col-title">Title</th>
                <th className="col-count">Count</th>
                <th className="col-time">Last seen</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map(alert => {
                const sevCfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.error;
                const catCfg = CATEGORY_CONFIG[alert.category] || { icon: '❓', label: alert.category };
                const isExpanded = expandedAlert === alert.id;

                return (
                  <React.Fragment key={alert.id}>
                    <tr
                      className={`alert-row ${sevCfg.className} ${alert.is_resolved ? 'resolved' : ''} ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                    >
                      <td className="col-select" onClick={(e) => e.stopPropagation()}>
                        {!alert.is_resolved && (
                          <input
                            type="checkbox"
                            checked={selectedAlerts.has(alert.id)}
                            onChange={() => toggleSelectAlert(alert.id)}
                          />
                        )}
                      </td>
                      <td className="col-severity">
                        <span className={`severity-badge ${sevCfg.className}`}>
                          {sevCfg.icon} {sevCfg.label}
                        </span>
                      </td>
                      <td className="col-category">
                        <span className="category-badge">
                          {catCfg.icon} {catCfg.label}
                        </span>
                      </td>
                      <td className="col-title">
                        <div className="alert-title">{alert.title}</div>
                        <div className="alert-message">{alert.message.substring(0, 120)}{alert.message.length > 120 ? '...' : ''}</div>
                      </td>
                      <td className="col-count">
                        <span className={`count-badge ${alert.occurrence_count > 5 ? 'high-count' : ''}`}>
                          {alert.occurrence_count}x
                        </span>
                      </td>
                      <td className="col-time">
                        <span className="time-ago">{timeAgo(alert.last_seen_at)}</span>
                      </td>
                      <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                        {!alert.is_resolved && (
                          <button
                            className="btn-resolve-small"
                            onClick={() => setShowResolveModal(alert.id)}
                            title="Resolve"
                          >
                            ✓
                          </button>
                        )}
                        <button
                          className="btn-delete-small"
                          onClick={() => handleDelete(alert.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      </td>
                    </tr>

                    {/* Expanded details */}
                    {isExpanded && (
                      <tr className="alert-details-row">
                        <td colSpan={7}>
                          <div className="alert-details">
                            <div className="details-grid">
                              <div className="detail-item">
                                <label>Error type:</label>
                                <span>{alert.error_type || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Source:</label>
                                <span>{alert.source_file || 'N/A'} → {alert.source_function || 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>First seen:</label>
                                <span>{formatDate(alert.first_seen_at)}</span>
                              </div>
                              <div className="detail-item">
                                <label>Last seen:</label>
                                <span>{formatDate(alert.last_seen_at)}</span>
                              </div>
                              {alert.user_id && (
                                <div className="detail-item">
                                  <label>User ID:</label>
                                  <span>{alert.user_id}</span>
                                </div>
                              )}
                              {alert.contact_id && (
                                <div className="detail-item">
                                  <label>Contact:</label>
                                  <span>{alert.contact_id}</span>
                                </div>
                              )}
                              {alert.is_resolved && (
                                <>
                                  <div className="detail-item">
                                    <label>Resolved at:</label>
                                    <span>{formatDate(alert.resolved_at)}</span>
                                  </div>
                                  {alert.resolution_note && (
                                    <div className="detail-item">
                                      <label>Resolution note:</label>
                                      <span>{alert.resolution_note}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="detail-full">
                              <label>Full message:</label>
                              <pre className="detail-message">{alert.message}</pre>
                            </div>
                            {alert.stack_trace && (
                              <div className="detail-full">
                                <label>Stack trace:</label>
                                <pre className="detail-stacktrace">{alert.stack_trace}</pre>
                              </div>
                            )}
                            {alert.extra_data && (
                              <div className="detail-full">
                                <label>Extra data:</label>
                                <pre className="detail-extra">{JSON.stringify(alert.extra_data, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="alerts-pagination">
            <span>Total: {total} alerts</span>
            <div className="pagination-controls">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages || 1}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Resolve modal */}
      {showResolveModal && (
        <div className="resolve-modal-overlay" onClick={() => setShowResolveModal(null)}>
          <div className="resolve-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Resolve Alert #{showResolveModal}</h3>
            <textarea
              placeholder="Resolution note (optional)..."
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={3}
            />
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setShowResolveModal(null); setResolveNote(''); }}>
                Cancel
              </button>
              <button className="btn-resolve" onClick={() => handleResolve(showResolveModal)}>
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemAlerts;
